import jsCookies from "js-cookie";

import { atom, getDefaultStore, useAtomValue } from "jotai";
import { UserAccess } from "@hpo/client/models/User";
import JwtManager, { Jwt } from "@hpo/client/utilities/JwtManager";
import Exception from "@hpo/client/utilities/errors/Exception";
import createInjectable from "@hpo/client/utilities/createInjectable";

const store = getDefaultStore();

export default class WhoAmI {
  private stateAtom = atom<Jwt<UserAccess> | null>(null);

  constructor(private jwt: JwtManager<UserAccess>) {
    this.readCookies(false);
  }

  hasCookie() {
    const content = jsCookies.get("hpo_access_token");
    return !!content;
  }

  readCookies(preserve: boolean) {
    const content = jsCookies.get("hpo_access_token");
    if (content === undefined) {
      if (preserve) {
        // Do nothing
      } else {
        this.setState(null);
      }
    } else {
      try {
        const jwt = this.jwt.decode(content);
        this.setState(jwt);
      } catch {
        this.setState(null);
      }
    }
  }

  use() {
    return useAtomValue(this.stateAtom);
  }

  searchUser() {
    const state = store.get(this.stateAtom);
    if (state === null) return null;
    const user = state.getPayload();
    return user;
  }

  findUser() {
    const user = this.searchUser();
    if (!user) throw new NoUser({ cause: null });
    return user;
  }

  private isLoggedIn() {
    return !!this.searchUser();
  }

  useIsLoggedIn() {
    const state = useAtomValue(this.stateAtom);
    return state !== null;
  }

  useUser() {
    const state = useAtomValue(this.stateAtom);
    if (state === null) throw new NoUser({ cause: null });
    return state.getPayload();
  }

  private setState(state: Jwt<UserAccess> | null) {
    if (state === null) console.log("SET STATE NULL");
    store.set(this.stateAtom, (current) => {
      if (current === null) {
        if (state === null) return null;
        else return state;
      } else {
        if (state === null) return null;
        if (current.getId() === state.getId()) return current;
        return state;
      }
    });
  }
}

class NoUser extends Exception {}

export const [WhoAmIProvider, useWhoAmI] = createInjectable<WhoAmI>("WhoAmI");
export function useMe() {
  return useWhoAmI().useUser();
}
